import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { UserList } from '../components/users/UserList';
import { UserDetailForm } from '../components/users/UserDetailForm';
import { HelpSidebar } from '../components/help/HelpSidebar';
import { useUsers } from '../hooks/useUsers';
import type { CognitoUser } from '../types/cognitoUser';

const UserManagement: React.FC = () => {
  const {
    users,
    selectedUser,
    isLoading,
    error,
    globalFilter,
    setGlobalFilter,
    setSelectedUser,
    loadUser,
    createUser,
    updateUser,
    deleteUser,
    disableUser,
    enableUser,
  } = useUsers();

  const toast = useRef<Toast>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);

  const handleRowClick = async (user: CognitoUser) => {
    const fullUser = await loadUser(user.username);
    if (fullUser) {
      setFormVisible(true);
    } else {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load user details',
        life: 3000,
      });
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormVisible(true);
  };

  const handleHideForm = () => {
    setFormVisible(false);
    setSelectedUser(null);
  };

  // Show error toast when store error changes
  React.useEffect(() => {
    if (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 5000,
      });
    }
  }, [error]);

  return (
    <div>
      <Toast ref={toast} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem 1rem 0' }}>
        <Button
          icon="pi pi-question-circle"
          className="p-button-text p-button-rounded"
          tooltip="Help"
          onClick={() => setHelpVisible(true)}
        />
      </div>
      <UserList
        users={users}
        isLoading={isLoading}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        onRowClick={handleRowClick}
        onCreateUser={handleCreateUser}
      />
      <UserDetailForm
        visible={formVisible}
        user={selectedUser}
        onHide={handleHideForm}
        onCreateUser={createUser}
        onUpdateUser={updateUser}
        onDeleteUser={deleteUser}
        onDisableUser={disableUser}
        onEnableUser={enableUser}
      />
      <HelpSidebar
        visible={helpVisible}
        onHide={() => setHelpVisible(false)}
        isAdmin={false}
        isUserAdmin={true}
      />
    </div>
  );
};

export default UserManagement;
